import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, MapPin, Building, Home, Landmark, Grid3X3, Car, Sofa, Users, Tag, ChevronRight, ChevronDown, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared dark-themed components
const FilterSection = ({ title, children }) => (
  <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4">
    <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">{title}</label>
    {children}
  </div>
);

const FilterPill = ({ selected, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all active:scale-95 ${
      selected ? 'bg-[#E31C25] text-white shadow-lg shadow-[#E31C25]/20' : 'bg-white/[0.06] text-white/50 border border-white/[0.08] hover:bg-white/[0.1]'
    }`}>{children}</button>
);

const FilterInput = ({ placeholder, value, onChange, type = 'text', suffix, testId }) => (
  <div className="relative">
    <input type={type} placeholder={placeholder} value={value || ''} onChange={onChange}
      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 text-white placeholder-white/25 text-sm transition-all"
      data-testid={testId} />
    {suffix && <span className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2 text-white/25 text-xs font-bold" dir="ltr">{suffix}</span>}
  </div>
);

const FilterSelect = ({ value, onChange, placeholder, options, testId }) => (
  <select value={value || ''} onChange={onChange}
    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none focus:border-[#E31C25]/50 text-white text-sm transition-all appearance-none"
    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
    data-testid={testId}>
    <option value="" className="bg-[#1A1A2E] text-white/50">{placeholder}</option>
    {options.map(opt => <option key={typeof opt === 'string' ? opt : opt.id} value={typeof opt === 'string' ? opt : opt.id} className="bg-[#1A1A2E] text-white">{typeof opt === 'string' ? opt : opt.label}</option>)}
  </select>
);

const RangeSlider = ({ label, min, max, step, value, onChange, formatVal }) => {
  const [localVal, setLocalVal] = React.useState(value);
  const dragging = React.useRef(false);
  React.useEffect(() => { if (!dragging.current) setLocalVal(value); }, [value]);
  const pct = ((localVal - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex justify-between text-[10px] text-white/30 mb-1.5">
        <span>{label}</span>
        <span className="text-white/50 font-semibold">{formatVal(localVal)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={localVal}
        onInput={e => { dragging.current = true; setLocalVal(Number(e.target.value)); }}
        onChange={e => {}}
        onMouseUp={e => { dragging.current = false; onChange(e); }}
        onTouchEnd={e => { dragging.current = false; onChange({ target: { value: String(localVal) } }); }}
        className="w-full h-2 rounded-full appearance-none cursor-pointer range-slider touch-none"
        style={{ background: `linear-gradient(to right, #E31C25 ${pct}%, rgba(255,255,255,0.1) ${pct}%)` }} />
      <style>{`
        .range-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 28px; height: 28px; border-radius: 50%; background: #E31C25; border: 3px solid #fff; cursor: pointer; box-shadow: 0 2px 8px rgba(227,28,37,0.4); }
        .range-slider::-moz-range-thumb { width: 28px; height: 28px; border-radius: 50%; background: #E31C25; border: 3px solid #fff; cursor: pointer; box-shadow: 0 2px 8px rgba(227,28,37,0.4); }
      `}</style>
    </div>
  );
};

const FiltersModal = ({ isOpen, onClose, filters, onApply, resultCount = 0, filterType: filterTypeProp, category = '' }) => {
  const { i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('rent');
  const [localFilters, setLocalFilters] = useState(filters || {});
  const [locations, setLocations] = useState([]);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showAllMakes, setShowAllMakes] = useState(false);
  const isAr = i18n.language === 'ar';

  // Use the passed filterType prop directly, with fallback to name-based detection
  const getFilterType = () => {
    if (filterTypeProp && filterTypeProp !== 'general') return filterTypeProp;
    const cat = category?.toLowerCase() || '';
    if (cat.includes('property') || cat.includes('rent') || cat.includes('sale') || cat.includes('off-plan') || cat.includes('rooms')) return 'property';
    if (cat.includes('motor') || cat.includes('car')) return 'motors';
    if (cat.includes('job')) return 'jobs';
    if (cat.includes('furniture') || cat.includes('garden')) return 'furniture';
    if (cat.includes('classified') || cat.includes('electronic') || cat.includes('fashion') || cat.includes('pet') || cat.includes('baby') || cat.includes('hobb') || cat.includes('sport')) return 'classifieds';
    if (cat.includes('community') || cat.includes('service')) return 'community';
    return 'general';
  };
  const filterType = getFilterType();

  // ========== DATA OPTIONS ==========
  const propertyTabs = [
    { id: 'rent', label: isAr ? 'إيجار' : 'Rent' },
    { id: 'buy', label: isAr ? 'شراء' : 'Buy' },
    { id: 'offplan', label: isAr ? 'على الخريطة' : 'Off-Plan' },
  ];
  const propertyTypes = [
    { id: 'residential', icon: Home, label: isAr ? 'سكني' : 'Residential' },
    { id: 'commercial', icon: Building, label: isAr ? 'تجاري' : 'Commercial' },
    { id: 'land', icon: Landmark, label: isAr ? 'أراضي' : 'Land' },
    { id: 'multiple', icon: Grid3X3, label: isAr ? 'وحدات متعددة' : 'Multiple Units' },
  ];
  const residentialCategories = [
    { id: 'penthouse', label: isAr ? 'بنتهاوس' : 'Penthouse' },
    { id: 'townhouse', label: isAr ? 'تاون هاوس' : 'Townhouse' },
    { id: 'villa', label: isAr ? 'فيلا' : 'Villa' },
    { id: 'apartment', label: isAr ? 'شقة' : 'Apartment' },
    { id: 'duplex', label: isAr ? 'دوبلكس' : 'Duplex' },
    { id: 'loft', label: isAr ? 'لوفت' : 'Loft' },
  ];
  const allAmenities = [
    { id: 'balcony', label: isAr ? 'شرفة' : 'Balcony' },
    { id: 'private_garden', label: isAr ? 'حديقة خاصة' : 'Private Garden' },
    { id: 'private_pool', label: isAr ? 'مسبح خاص' : 'Private Pool' },
    { id: 'shared_pool', label: isAr ? 'مسبح مشترك' : 'Shared Pool' },
    { id: 'maids_room', label: isAr ? 'غرفة خادمة' : 'Maids Room' },
    { id: 'central_ac', label: isAr ? 'تكييف مركزي' : 'Central A/C' },
    { id: 'parking', label: isAr ? 'موقف سيارات' : 'Parking' },
    { id: 'security', label: isAr ? 'أمن' : 'Security' },
    { id: 'built_in_wardrobes', label: isAr ? 'خزائن مدمجة' : 'Wardrobes' },
    { id: 'concierge', label: isAr ? 'كونسيرج' : 'Concierge' },
    { id: 'gym', label: isAr ? 'صالة رياضية' : 'Gym' },
    { id: 'kids_play_area', label: isAr ? 'منطقة ألعاب' : 'Kids Play Area' },
  ];
  const furnishingTypes = [
    { id: 'all', label: isAr ? 'الكل' : 'All' },
    { id: 'furnished', label: isAr ? 'مفروش' : 'Furnished' },
    { id: 'unfurnished', label: isAr ? 'غير مفروش' : 'Unfurnished' },
  ];

  // Motors
  const carMakes = [
    'Abarth', 'Acura', 'Alfa Romeo', 'Aston Martin', 'Audi', 'Bentley', 'BMW', 'BYD',
    'Cadillac', 'Changan', 'Chery', 'Chevrolet', 'Chrysler', 'Citroen', 'Cupra',
    'Dodge', 'Ferrari', 'Fiat', 'Ford', 'GAC', 'Geely', 'Genesis', 'GMC',
    'Great Wall', 'Haval', 'Honda', 'Hyundai', 'Infiniti', 'Isuzu',
    'Jaguar', 'Jeep', 'Jetour', 'Kia', 'Lamborghini', 'Land Rover',
    'Lexus', 'Lincoln', 'Lotus', 'Lucid', 'Maserati', 'Mazda', 'McLaren',
    'Mercedes-Benz', 'MG', 'Mini', 'Mitsubishi', 'Nissan',
    'Peugeot', 'Polestar', 'Porsche', 'Ram', 'Renault', 'Rivian',
    'Rolls-Royce', 'Skoda', 'Subaru', 'Suzuki', 'Tesla', 'Toyota',
    'Volkswagen', 'Volvo', 'XPeng', 'Zeekr',
  ];
  const carYears = Array.from({ length: 30 }, (_, i) => (2026 - i).toString());
  const bodyTypes = [
    { id: 'Sedan', label: isAr ? 'سيدان' : 'Sedan' }, { id: 'SUV', label: isAr ? 'دفع رباعي' : 'SUV' },
    { id: 'Coupe', label: isAr ? 'كوبيه' : 'Coupe' }, { id: 'Hatchback', label: isAr ? 'هاتشباك' : 'Hatchback' },
    { id: 'Pickup', label: isAr ? 'بيك أب' : 'Pickup' }, { id: 'Van', label: isAr ? 'فان' : 'Van' },
    { id: 'Convertible', label: isAr ? 'مكشوفة' : 'Convertible' }, { id: 'Wagon', label: isAr ? 'واغن' : 'Wagon' },
    { id: 'Crossover', label: isAr ? 'كروسوفر' : 'Crossover' },
  ];
  const fuelTypes = [
    { id: 'Petrol', label: isAr ? 'بنزين' : 'Petrol' }, { id: 'Diesel', label: isAr ? 'ديزل' : 'Diesel' },
    { id: 'Hybrid', label: isAr ? 'هجين' : 'Hybrid' }, { id: 'Electric', label: isAr ? 'كهربائي' : 'Electric' },
  ];
  const transmissions = [
    { id: 'Automatic', label: isAr ? 'أوتوماتيك' : 'Automatic' },
    { id: 'Manual', label: isAr ? 'يدوي' : 'Manual' },
  ];
  const sellerTypes = [
    { id: 'owner', label: isAr ? 'مالك' : 'Owner' },
    { id: 'dealer', label: isAr ? 'تاجر' : 'Dealer' },
  ];
  const exteriorColors = ['White', 'Black', 'Silver', 'Grey', 'Red', 'Blue', 'Brown', 'Gold', 'Green', 'Orange', 'Yellow', 'Beige', 'Burgundy'];
  const colorMap = { White: '#fff', Black: '#222', Silver: '#C0C0C0', Grey: '#808080', Red: '#E31C25', Blue: '#3b82f6', Brown: '#8B4513', Gold: '#FFD700', Green: '#22c55e', Orange: '#f97316', Yellow: '#eab308', Beige: '#F5F5DC', Burgundy: '#800020' };
  const mileageRanges = [
    { id: '5000', label: '0 - 5,000 km' }, { id: '10000', label: '5,000 - 10,000 km' },
    { id: '50000', label: '10,000 - 50,000 km' }, { id: '100000', label: '50,000 - 100,000 km' },
    { id: '200000', label: '100,000 - 200,000 km' }, { id: '200001', label: '200,000+ km' },
  ];

  // Jobs
  const jobTypes = [
    { id: 'Full Time', label: isAr ? 'دوام كامل' : 'Full Time' },
    { id: 'Part Time', label: isAr ? 'دوام جزئي' : 'Part Time' },
    { id: 'Contract', label: isAr ? 'عقد' : 'Contract' },
    { id: 'Freelance', label: isAr ? 'عمل حر' : 'Freelance' },
    { id: 'Temporary', label: isAr ? 'مؤقت' : 'Temporary' },
    { id: 'Internship', label: isAr ? 'تدريب' : 'Internship' },
  ];
  const experienceLevels = [
    { id: 'No Experience', label: isAr ? 'بدون خبرة' : 'No Experience' },
    { id: '1-2 Years', label: isAr ? '١-٢ سنوات' : '1-2 Years' },
    { id: '3-5 Years', label: isAr ? '٣-٥ سنوات' : '3-5 Years' },
    { id: '6-10 Years', label: isAr ? '٦-١٠ سنوات' : '6-10 Years' },
    { id: '10+ Years', label: isAr ? '١٠+ سنوات' : '10+ Years' },
  ];
  const jobIndustries = [
    { id: 'accounting-finance', label: isAr ? 'المحاسبة والمالية' : 'Accounting & Finance' },
    { id: 'engineering', label: isAr ? 'الهندسة' : 'Engineering' },
    { id: 'it-technology', label: isAr ? 'تقنية المعلومات' : 'IT & Technology' },
    { id: 'sales-marketing', label: isAr ? 'المبيعات والتسويق' : 'Sales & Marketing' },
    { id: 'customer-service', label: isAr ? 'خدمة العملاء' : 'Customer Service' },
    { id: 'administration', label: isAr ? 'الإدارة' : 'Administration' },
    { id: 'hospitality', label: isAr ? 'الضيافة' : 'Hospitality & Tourism' },
    { id: 'construction', label: isAr ? 'البناء' : 'Construction' },
    { id: 'healthcare', label: isAr ? 'الرعاية الصحية' : 'Healthcare' },
    { id: 'education', label: isAr ? 'التعليم' : 'Education' },
    { id: 'logistics', label: isAr ? 'اللوجستيات' : 'Logistics & Transport' },
    { id: 'real-estate', label: isAr ? 'العقارات' : 'Real Estate' },
    { id: 'media', label: isAr ? 'الإعلام' : 'Media & Creative' },
    { id: 'legal', label: isAr ? 'القانون' : 'Legal' },
    { id: 'other', label: isAr ? 'أخرى' : 'Other' },
  ];

  // General
  const conditionOptions = [
    { id: 'all', label: isAr ? 'الكل' : 'All' },
    { id: 'new', label: isAr ? 'جديد' : 'New' },
    { id: 'used', label: isAr ? 'مستعمل' : 'Used' },
  ];

  // Motorcycles
  const motorcycleBrands = [
    'Aprilia', 'Benelli', 'BMW', 'Can-Am', 'Ducati', 'Harley-Davidson', 'Honda',
    'Husqvarna', 'Indian', 'Kawasaki', 'KTM', 'Piaggio', 'Royal Enfield',
    'Suzuki', 'Triumph', 'Vespa', 'Yamaha',
  ];
  const motorcycleTypes = [
    { id: 'adventure-touring', label: isAr ? 'مغامرات / جولات' : 'Adventure / Touring' },
    { id: 'cafe-racer', label: isAr ? 'كافيه ريسر' : 'Cafe Racer' },
    { id: 'cruiser-chopper', label: isAr ? 'كروزر / تشوبر' : 'Cruiser / Chopper' },
    { id: 'off-road', label: isAr ? 'طرق وعرة' : 'Off-Road' },
    { id: 'scooter', label: isAr ? 'سكوتر' : 'Scooter' },
    { id: 'sport-bike', label: isAr ? 'دراجة رياضية' : 'Sport Bike' },
    { id: 'standard-commuter', label: isAr ? 'قياسي / تنقل' : 'Standard / Commuter' },
    { id: 'trike', label: isAr ? 'ثلاثية العجلات' : 'Trike' },
  ];
  const engineSizes = [
    { id: '50-125', label: '50 - 125 cc' }, { id: '125-250', label: '125 - 250 cc' },
    { id: '250-500', label: '250 - 500 cc' }, { id: '500-750', label: '500 - 750 cc' },
    { id: '750-1000', label: '750 - 1000 cc' }, { id: '1000+', label: '1000+ cc' },
  ];

  // Boats
  const boatTypes = [
    { id: 'motorboats', label: isAr ? 'قوارب بمحرك' : 'Motorboats' },
    { id: 'sailboats', label: isAr ? 'قوارب شراعية' : 'Sailboats' },
    { id: 'jet-ski', label: isAr ? 'جت سكي' : 'Jet Ski' },
    { id: 'yacht', label: isAr ? 'يخت' : 'Yacht' },
    { id: 'fishing-boat', label: isAr ? 'قارب صيد' : 'Fishing Boat' },
    { id: 'row-paddle', label: isAr ? 'قوارب تجديف' : 'Row / Paddle' },
  ];
  const boatLengths = [
    { id: 'under-20', label: isAr ? 'أقل من 20 قدم' : 'Under 20 ft' },
    { id: '20-30', label: '20 - 30 ft' }, { id: '30-40', label: '30 - 40 ft' },
    { id: '40-50', label: '40 - 50 ft' }, { id: '50-75', label: '50 - 75 ft' },
    { id: '75+', label: '75+ ft' },
  ];
  const boatEngineTypes = [
    { id: 'inboard', label: isAr ? 'داخلي' : 'Inboard' },
    { id: 'outboard', label: isAr ? 'خارجي' : 'Outboard' },
    { id: 'jet-drive', label: isAr ? 'جت' : 'Jet Drive' },
    { id: 'sail', label: isAr ? 'شراع' : 'Sail' },
  ];

  // Heavy Vehicles
  const heavyVehicleTypes = [
    { id: 'trucks', label: isAr ? 'شاحنات' : 'Trucks' },
    { id: 'buses', label: isAr ? 'حافلات' : 'Buses' },
    { id: 'cranes', label: isAr ? 'رافعات' : 'Cranes' },
    { id: 'forklifts', label: isAr ? 'رافعات شوكية' : 'Forklifts' },
    { id: 'trailers', label: isAr ? 'مقطورات' : 'Trailers' },
    { id: 'tankers', label: isAr ? 'صهاريج' : 'Tankers' },
    { id: 'aircrafts', label: isAr ? 'طائرات' : 'Aircrafts' },
    { id: 'parts-engines', label: isAr ? 'قطع ومحركات' : 'Parts & Engines' },
  ];
  const heavyWeightCapacity = [
    { id: 'under-5', label: isAr ? 'أقل من 5 طن' : 'Under 5 tons' },
    { id: '5-10', label: '5 - 10 tons' }, { id: '10-20', label: '10 - 20 tons' },
    { id: '20-40', label: '20 - 40 tons' }, { id: '40+', label: '40+ tons' },
  ];

  // Auto Accessories
  const accessoryTypes = [
    { id: 'car-4x4-parts', label: isAr ? 'قطع سيارات/دفع رباعي' : 'Car / 4x4 Parts' },
    { id: 'motorcycle-parts', label: isAr ? 'قطع دراجات نارية' : 'Motorcycle Parts' },
    { id: 'boat-parts', label: isAr ? 'قطع قوارب' : 'Boat Parts' },
    { id: 'automotive-tools', label: isAr ? 'أدوات سيارات' : 'Automotive Tools' },
    { id: 'apparel-merchandise', label: isAr ? 'ملابس وإكسسوارات' : 'Apparel & Merchandise' },
    { id: 'other-vehicle-parts', label: isAr ? 'قطع مركبات أخرى' : 'Other Vehicle Parts' },
  ];

  // Number Plates
  const plateEmirateOptions = [
    { id: 'Dubai', label: isAr ? 'دبي' : 'Dubai' },
    { id: 'Abu Dhabi', label: isAr ? 'أبوظبي' : 'Abu Dhabi' },
    { id: 'Sharjah', label: isAr ? 'الشارقة' : 'Sharjah' },
    { id: 'Ajman', label: isAr ? 'عجمان' : 'Ajman' },
    { id: 'Ras Al Khaimah', label: isAr ? 'رأس الخيمة' : 'Ras Al Khaimah' },
    { id: 'Fujairah', label: isAr ? 'الفجيرة' : 'Fujairah' },
    { id: 'Umm Al Quwain', label: isAr ? 'أم القيوين' : 'Umm Al Quwain' },
  ];
  const plateCodes = [
    { id: 'A', label: 'A' }, { id: 'B', label: 'B' }, { id: 'C', label: 'C' },
    { id: 'D', label: 'D' }, { id: 'E', label: 'E' }, { id: 'F', label: 'F' },
    { id: 'G', label: 'G' }, { id: 'H', label: 'H' }, { id: 'I', label: 'I' },
    { id: 'J', label: 'J' }, { id: 'K', label: 'K' }, { id: 'L', label: 'L' },
    { id: 'M', label: 'M' }, { id: 'N', label: 'N' }, { id: 'O', label: 'O' },
    { id: 'P', label: 'P' }, { id: 'Q', label: 'Q' }, { id: 'R', label: 'R' },
    { id: 'S', label: 'S' }, { id: 'T', label: 'T' }, { id: 'U', label: 'U' },
    { id: 'V', label: 'V' }, { id: 'W', label: 'W' }, { id: 'X', label: 'X' },
    { id: 'Y', label: 'Y' }, { id: 'Z', label: 'Z' },
  ];
  const plateDigitOptions = [
    { id: '1', label: isAr ? 'رقم واحد' : '1 Digit' },
    { id: '2', label: isAr ? 'رقمين' : '2 Digits' },
    { id: '3', label: isAr ? '3 أرقام' : '3 Digits' },
    { id: '4', label: isAr ? '4 أرقام' : '4 Digits' },
    { id: '5', label: isAr ? '5 أرقام' : '5 Digits' },
  ];
  const furnitureTypes = [
    { id: 'sofas', label: isAr ? 'كنب' : 'Sofas' }, { id: 'beds', label: isAr ? 'أسرة' : 'Beds' },
    { id: 'tables', label: isAr ? 'طاولات' : 'Tables' }, { id: 'storage', label: isAr ? 'تخزين' : 'Storage' },
    { id: 'outdoor', label: isAr ? 'خارجي' : 'Outdoor' }, { id: 'office', label: isAr ? 'مكتبي' : 'Office' },
    { id: 'lighting', label: isAr ? 'إضاءة' : 'Lighting' }, { id: 'rugs', label: isAr ? 'سجاد' : 'Rugs & Carpets' },
  ];
  const classifiedTypes = [
    { id: 'electronics', label: isAr ? 'إلكترونيات' : 'Electronics' }, { id: 'fashion', label: isAr ? 'أزياء' : 'Fashion' },
    { id: 'sports', label: isAr ? 'رياضة' : 'Sports' }, { id: 'baby', label: isAr ? 'أطفال' : 'Baby & Kids' },
    { id: 'pets', label: isAr ? 'حيوانات' : 'Pets' }, { id: 'hobbies', label: isAr ? 'هوايات' : 'Hobbies' },
    { id: 'other', label: isAr ? 'أخرى' : 'Other' },
  ];
  const communityTypes = [
    { id: 'activities', label: isAr ? 'أنشطة' : 'Activities' }, { id: 'classes', label: isAr ? 'دورات' : 'Classes' },
    { id: 'services', label: isAr ? 'خدمات' : 'Services' }, { id: 'freelancers', label: isAr ? 'مستقلين' : 'Freelancers' },
    { id: 'car-lift', label: isAr ? 'مشاركة سيارة' : 'Car Lift' }, { id: 'events', label: isAr ? 'فعاليات' : 'Events' },
  ];

  useEffect(() => { fetchLocations(); }, []);
  useEffect(() => { setLocalFilters(filters || {}); }, [filters]);

  const fetchLocations = async () => {
    try { const res = await axios.get(`${API}/locations`); setLocations(res.data); } catch (e) { console.error(e); }
  };

  const handleFilterChange = (key, value) => setLocalFilters(prev => ({ ...prev, [key]: value }));
  const handleReset = () => { setLocalFilters({}); setActiveTab('rent'); };
  const handleApply = () => { onApply({ ...localFilters, listingType: activeTab }); onClose(); };
  const displayedAmenities = showAllAmenities ? allAmenities : allAmenities.slice(0, 6);
  const displayedMakes = showAllMakes ? carMakes : carMakes.slice(0, 12);

  if (!isOpen) return null;

  const fmtPrice = (v) => v ? `AED ${parseInt(v).toLocaleString()}` : 'AED 0';

  // Reusable sections
  const PriceRange = ({ maxVal = 2000000, step = 5000 }) => {
    // Auto-calculate step to always have ~20-30 stops (fast finger swipe)
    const smartStep = Math.max(step, Math.round(maxVal / 25 / 1000) * 1000);
    // Quick-select presets based on max value
    const presets = maxVal >= 5000000
      ? [50000, 200000, 500000, 1000000, 3000000, 5000000]
      : maxVal >= 500000
        ? [10000, 50000, 100000, 250000, 500000]
        : [1000, 5000, 10000, 25000, 50000, 100000];

    return (
      <FilterSection title={isAr ? 'نطاق السعر' : 'Price Range'}>
        <div className="grid grid-cols-2 gap-2.5 mb-3">
          <FilterInput type="number" placeholder={isAr ? 'الحد الأدنى' : 'Min'} suffix="AED"
            value={localFilters.minPrice} onChange={e => handleFilterChange('minPrice', e.target.value)} testId="filter-min-price" />
          <FilterInput type="number" placeholder={isAr ? 'الحد الأقصى' : 'Max'} suffix="AED"
            value={localFilters.maxPrice} onChange={e => handleFilterChange('maxPrice', e.target.value)} testId="filter-max-price" />
        </div>
        <div className="space-y-3">
          <RangeSlider label={isAr ? 'الحد الأدنى' : 'Min'} min={0} max={maxVal} step={smartStep} value={localFilters.minPrice || 0} onChange={e => handleFilterChange('minPrice', e.target.value)} formatVal={fmtPrice} />
          <RangeSlider label={isAr ? 'الحد الأقصى' : 'Max'} min={0} max={maxVal} step={smartStep} value={localFilters.maxPrice || maxVal} onChange={e => handleFilterChange('maxPrice', e.target.value)} formatVal={fmtPrice} />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {presets.map(p => (
            <button key={p} onClick={() => handleFilterChange('maxPrice', p.toString())}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${parseInt(localFilters.maxPrice) === p ? 'bg-[#E31C25] text-white' : 'bg-white/[0.06] text-white/40 border border-white/[0.08] hover:bg-white/[0.1]'}`}>
              {p >= 1000000 ? `${p / 1000000}M` : `${(p / 1000).toFixed(0)}K`}
            </button>
          ))}
        </div>
      </FilterSection>
    );
  };

  const LocationSection = () => (
    <FilterSection title={isAr ? 'المدينة' : 'City'}>
      <FilterSelect value={localFilters.location} onChange={e => handleFilterChange('location', e.target.value)}
        placeholder={isAr ? 'كل الإمارات' : 'All Emirates'} options={locations.map(l => ({ id: l.city, label: l.city }))} testId="filter-location" />
    </FilterSection>
  );

  const ConditionSection = () => (
    <FilterSection title={isAr ? 'الحالة' : 'Condition'}>
      <div className="flex gap-2">
        {conditionOptions.map(c => <FilterPill key={c.id} selected={(localFilters.condition || 'all') === c.id} onClick={() => handleFilterChange('condition', c.id)}>{c.label}</FilterPill>)}
      </div>
    </FilterSection>
  );

  const KeywordSection = () => (
    <FilterSection title={isAr ? 'كلمات مفتاحية' : 'Keywords'}>
      <FilterInput placeholder={isAr ? 'ابحث عن...' : 'Search for...'} value={localFilters.keywords} onChange={e => handleFilterChange('keywords', e.target.value)} testId="filter-keywords" />
    </FilterSection>
  );

  // ========== PROPERTY FILTERS ==========
  const renderPropertyFilters = () => (
    <>
      <div className="flex border-b border-white/[0.06]">
        {propertyTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 text-sm font-semibold transition-all ${activeTab === tab.id ? 'text-[#E31C25] border-b-2 border-[#E31C25]' : 'text-white/40'}`}>{tab.label}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <LocationSection />
        <FilterSection title={isAr ? 'نوع العقار' : 'Property Type'}>
          <div className="grid grid-cols-4 gap-2">
            {propertyTypes.map(t => { const Icon = t.icon; const sel = localFilters.propertyType === t.id; return (
              <button key={t.id} onClick={() => handleFilterChange('propertyType', sel ? '' : t.id)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${sel ? 'border-[#E31C25] bg-[#E31C25]/10' : 'border-white/[0.08] bg-white/[0.03]'}`}>
                <Icon className={`w-5 h-5 ${sel ? 'text-[#E31C25]' : 'text-white/30'}`} />
                <span className={`text-[10px] font-semibold ${sel ? 'text-white' : 'text-white/40'}`}>{t.label}</span>
              </button>);
            })}
          </div>
        </FilterSection>
        <FilterSection title={isAr ? 'فئات سكنية' : 'Category'}>
          <div className="flex flex-wrap gap-1.5">
            {residentialCategories.map(t => <FilterPill key={t.id} selected={localFilters.residentialType === t.id} onClick={() => handleFilterChange('residentialType', localFilters.residentialType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
          </div>
        </FilterSection>
        <PriceRange maxVal={10000000} step={10000} />
        <FilterSection title={isAr ? 'غرف النوم' : 'Bedrooms'}>
          <div className="flex flex-wrap gap-1.5">
            {['Studio', '1', '2', '3', '4', '5', '6', '7+'].map(n => <FilterPill key={n} selected={localFilters.bedrooms === n} onClick={() => handleFilterChange('bedrooms', localFilters.bedrooms === n ? '' : n)}>{n}</FilterPill>)}
          </div>
        </FilterSection>
        <FilterSection title={isAr ? 'الحمامات' : 'Bathrooms'}>
          <div className="flex flex-wrap gap-1.5">
            {['1', '2', '3', '4', '5+'].map(n => <FilterPill key={n} selected={localFilters.bathrooms === n} onClick={() => handleFilterChange('bathrooms', localFilters.bathrooms === n ? '' : n)}>{n}</FilterPill>)}
          </div>
        </FilterSection>
        <FilterSection title={isAr ? 'المساحة (قدم مربع)' : 'Size (sqft)'}>
          <div className="grid grid-cols-2 gap-2.5">
            <FilterInput type="number" placeholder={isAr ? 'الحد الأدنى' : 'Min sqft'} value={localFilters.minSize} onChange={e => handleFilterChange('minSize', e.target.value)} testId="filter-min-size" />
            <FilterInput type="number" placeholder={isAr ? 'الحد الأقصى' : 'Max sqft'} value={localFilters.maxSize} onChange={e => handleFilterChange('maxSize', e.target.value)} testId="filter-max-size" />
          </div>
        </FilterSection>
        <FilterSection title={isAr ? 'التأثيث' : 'Furnishing'}>
          <div className="flex gap-2">
            {furnishingTypes.map(t => <FilterPill key={t.id} selected={localFilters.furnishing === t.id} onClick={() => handleFilterChange('furnishing', t.id)}>{t.label}</FilterPill>)}
          </div>
        </FilterSection>
        <FilterSection title={isAr ? 'المرافق' : 'Amenities'}>
          <div className="flex flex-wrap gap-1.5">
            {displayedAmenities.map(a => {
              const sel = (localFilters.amenities || []).includes(a.id);
              return <FilterPill key={a.id} selected={sel} onClick={() => {
                const cur = localFilters.amenities || [];
                handleFilterChange('amenities', sel ? cur.filter(x => x !== a.id) : [...cur, a.id]);
              }}>{a.label}</FilterPill>;
            })}
          </div>
          <button onClick={() => setShowAllAmenities(!showAllAmenities)} className="text-[#E31C25] text-xs font-semibold mt-2 flex items-center gap-1">
            {showAllAmenities ? (isAr ? 'عرض أقل' : 'Show less') : (isAr ? 'عرض الكل' : 'View all')}
            <ChevronRight className={`w-3 h-3 transition-transform ${showAllAmenities ? 'rotate-90' : ''}`} />
          </button>
        </FilterSection>
        <KeywordSection />
      </div>
    </>
  );

  // ========== MOTORS FILTERS (Cars only) ==========
  const renderCarsFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'الماركة' : 'Make'}>
        <div className="flex flex-wrap gap-1.5">
          {displayedMakes.map(m => <FilterPill key={m} selected={localFilters.make === m} onClick={() => handleFilterChange('make', localFilters.make === m ? '' : m)}>{m}</FilterPill>)}
        </div>
        <button onClick={() => setShowAllMakes(!showAllMakes)} className="text-[#E31C25] text-xs font-semibold mt-2 flex items-center gap-1">
          {showAllMakes ? (isAr ? 'عرض أقل' : 'Show less') : (isAr ? 'عرض الكل' : `View all (${carMakes.length})`)}
          <ChevronDown className={`w-3 h-3 transition-transform ${showAllMakes ? 'rotate-180' : ''}`} />
        </button>
      </FilterSection>
      <FilterSection title={isAr ? 'نوع الهيكل' : 'Body Type'}>
        <div className="flex flex-wrap gap-1.5">
          {bodyTypes.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('bodyType', localFilters.bodyType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'سنة الصنع' : 'Year'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterSelect value={localFilters.minYear} onChange={e => handleFilterChange('minYear', e.target.value)} placeholder={isAr ? 'من' : 'From'} options={carYears.map(y => ({ id: y, label: y }))} testId="filter-min-year" />
          <FilterSelect value={localFilters.maxYear} onChange={e => handleFilterChange('maxYear', e.target.value)} placeholder={isAr ? 'إلى' : 'To'} options={carYears.map(y => ({ id: y, label: y }))} testId="filter-max-year" />
        </div>
      </FilterSection>
      <PriceRange />
      <FilterSection title={isAr ? 'الكيلومترات' : 'Kilometers'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterInput type="number" placeholder={isAr ? 'الحد الأدنى' : 'Min KM'} value={localFilters.minKm} onChange={e => handleFilterChange('minKm', e.target.value)} testId="filter-min-km" />
          <FilterInput type="number" placeholder={isAr ? 'الحد الأقصى' : 'Max KM'} value={localFilters.maxKm} onChange={e => handleFilterChange('maxKm', e.target.value)} testId="filter-max-km" />
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'نوع الوقود' : 'Fuel Type'}>
        <div className="flex flex-wrap gap-1.5">
          {fuelTypes.map(t => <FilterPill key={t.id} selected={localFilters.fuelType === t.id} onClick={() => handleFilterChange('fuelType', localFilters.fuelType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'ناقل الحركة' : 'Transmission'}>
        <div className="flex gap-2">
          {transmissions.map(t => <FilterPill key={t.id} selected={localFilters.transmission === t.id} onClick={() => handleFilterChange('transmission', localFilters.transmission === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'اللون الخارجي' : 'Exterior Color'}>
        <div className="flex flex-wrap gap-1.5">
          {exteriorColors.map(c => (
            <button key={c} onClick={() => handleFilterChange('color', localFilters.color === c ? '' : c)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold transition-all ${localFilters.color === c ? 'bg-[#E31C25] text-white' : 'bg-white/[0.06] text-white/50 border border-white/[0.08]'}`}>
              <span className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: colorMap[c] }} />
              {c}
            </button>
          ))}
        </div>
      </FilterSection>
      <ConditionSection />
      <FilterSection title={isAr ? 'نوع البائع' : 'Seller Type'}>
        <div className="flex gap-2">
          {sellerTypes.map(t => <FilterPill key={t.id} selected={localFilters.sellerType === t.id} onClick={() => handleFilterChange('sellerType', localFilters.sellerType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <KeywordSection />
    </div>
  );

  // ========== MOTORCYCLES FILTERS ==========
  const renderMotorcyclesFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'الماركة' : 'Brand'}>
        <div className="flex flex-wrap gap-1.5">
          {motorcycleBrands.map(m => <FilterPill key={m} selected={localFilters.make === m} onClick={() => handleFilterChange('make', localFilters.make === m ? '' : m)}>{m}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'النوع' : 'Type'}>
        <div className="flex flex-wrap gap-1.5">
          {motorcycleTypes.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('bodyType', localFilters.bodyType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'حجم المحرك' : 'Engine Size'}>
        <div className="flex flex-wrap gap-1.5">
          {engineSizes.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('engineSize', localFilters.engineSize === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'سنة الصنع' : 'Year'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterSelect value={localFilters.minYear} onChange={e => handleFilterChange('minYear', e.target.value)} placeholder={isAr ? 'من' : 'From'} options={carYears.map(y => ({ id: y, label: y }))} />
          <FilterSelect value={localFilters.maxYear} onChange={e => handleFilterChange('maxYear', e.target.value)} placeholder={isAr ? 'إلى' : 'To'} options={carYears.map(y => ({ id: y, label: y }))} />
        </div>
      </FilterSection>
      <PriceRange maxVal={500000} step={1000} />
      <FilterSection title={isAr ? 'الكيلومترات' : 'Kilometers'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterInput type="number" placeholder={isAr ? 'الحد الأدنى' : 'Min KM'} value={localFilters.minKm} onChange={e => handleFilterChange('minKm', e.target.value)} />
          <FilterInput type="number" placeholder={isAr ? 'الحد الأقصى' : 'Max KM'} value={localFilters.maxKm} onChange={e => handleFilterChange('maxKm', e.target.value)} />
        </div>
      </FilterSection>
      <ConditionSection />
      <FilterSection title={isAr ? 'نوع البائع' : 'Seller Type'}>
        <div className="flex gap-2">
          {sellerTypes.map(t => <FilterPill key={t.id} selected={localFilters.sellerType === t.id} onClick={() => handleFilterChange('sellerType', localFilters.sellerType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <KeywordSection />
    </div>
  );

  // ========== BOATS FILTERS ==========
  const renderBoatsFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'نوع القارب' : 'Boat Type'}>
        <div className="flex flex-wrap gap-1.5">
          {boatTypes.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('bodyType', localFilters.bodyType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'الطول' : 'Length'}>
        <div className="flex flex-wrap gap-1.5">
          {boatLengths.map(t => <FilterPill key={t.id} selected={localFilters.engineSize === t.id} onClick={() => handleFilterChange('engineSize', localFilters.engineSize === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'نوع المحرك' : 'Engine Type'}>
        <div className="flex flex-wrap gap-1.5">
          {boatEngineTypes.map(t => <FilterPill key={t.id} selected={localFilters.fuelType === t.id} onClick={() => handleFilterChange('fuelType', localFilters.fuelType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'سنة الصنع' : 'Year'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterSelect value={localFilters.minYear} onChange={e => handleFilterChange('minYear', e.target.value)} placeholder={isAr ? 'من' : 'From'} options={carYears.map(y => ({ id: y, label: y }))} />
          <FilterSelect value={localFilters.maxYear} onChange={e => handleFilterChange('maxYear', e.target.value)} placeholder={isAr ? 'إلى' : 'To'} options={carYears.map(y => ({ id: y, label: y }))} />
        </div>
      </FilterSection>
      <PriceRange maxVal={5000000} step={10000} />
      <ConditionSection />
      <FilterSection title={isAr ? 'نوع البائع' : 'Seller Type'}>
        <div className="flex gap-2">
          {sellerTypes.map(t => <FilterPill key={t.id} selected={localFilters.sellerType === t.id} onClick={() => handleFilterChange('sellerType', localFilters.sellerType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <KeywordSection />
    </div>
  );

  // ========== HEAVY VEHICLES FILTERS ==========
  const renderHeavyVehiclesFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'النوع' : 'Vehicle Type'}>
        <div className="flex flex-wrap gap-1.5">
          {heavyVehicleTypes.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('bodyType', localFilters.bodyType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'الحمولة' : 'Weight Capacity'}>
        <div className="flex flex-wrap gap-1.5">
          {heavyWeightCapacity.map(t => <FilterPill key={t.id} selected={localFilters.engineSize === t.id} onClick={() => handleFilterChange('engineSize', localFilters.engineSize === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'سنة الصنع' : 'Year'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterSelect value={localFilters.minYear} onChange={e => handleFilterChange('minYear', e.target.value)} placeholder={isAr ? 'من' : 'From'} options={carYears.map(y => ({ id: y, label: y }))} />
          <FilterSelect value={localFilters.maxYear} onChange={e => handleFilterChange('maxYear', e.target.value)} placeholder={isAr ? 'إلى' : 'To'} options={carYears.map(y => ({ id: y, label: y }))} />
        </div>
      </FilterSection>
      <PriceRange maxVal={3000000} step={10000} />
      <FilterSection title={isAr ? 'الكيلومترات' : 'Kilometers'}>
        <div className="grid grid-cols-2 gap-2.5">
          <FilterInput type="number" placeholder={isAr ? 'الحد الأدنى' : 'Min KM'} value={localFilters.minKm} onChange={e => handleFilterChange('minKm', e.target.value)} />
          <FilterInput type="number" placeholder={isAr ? 'الحد الأقصى' : 'Max KM'} value={localFilters.maxKm} onChange={e => handleFilterChange('maxKm', e.target.value)} />
        </div>
      </FilterSection>
      <ConditionSection />
      <FilterSection title={isAr ? 'نوع البائع' : 'Seller Type'}>
        <div className="flex gap-2">
          {sellerTypes.map(t => <FilterPill key={t.id} selected={localFilters.sellerType === t.id} onClick={() => handleFilterChange('sellerType', localFilters.sellerType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <KeywordSection />
    </div>
  );

  // ========== AUTO ACCESSORIES FILTERS ==========
  const renderAccessoriesFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'نوع القطعة' : 'Part Type'}>
        <div className="flex flex-wrap gap-1.5">
          {accessoryTypes.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('bodyType', localFilters.bodyType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <ConditionSection />
      <PriceRange maxVal={100000} step={500} />
      <FilterSection title={isAr ? 'نوع البائع' : 'Seller Type'}>
        <div className="flex gap-2">
          {sellerTypes.map(t => <FilterPill key={t.id} selected={localFilters.sellerType === t.id} onClick={() => handleFilterChange('sellerType', localFilters.sellerType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <KeywordSection />
    </div>
  );

  // ========== NUMBER PLATES FILTERS ==========
  const renderNumberPlatesFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <FilterSection title={isAr ? 'الإمارة' : 'Emirate'}>
        <div className="flex flex-wrap gap-1.5">
          {plateEmirateOptions.map(t => <FilterPill key={t.id} selected={localFilters.location === t.id} onClick={() => handleFilterChange('location', localFilters.location === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'كود اللوحة' : 'Plate Code'}>
        <div className="flex flex-wrap gap-1.5">
          {plateCodes.map(t => <FilterPill key={t.id} selected={localFilters.make === t.id} onClick={() => handleFilterChange('make', localFilters.make === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'عدد الأرقام' : 'Number of Digits'}>
        <div className="flex flex-wrap gap-1.5">
          {plateDigitOptions.map(t => <FilterPill key={t.id} selected={localFilters.bodyType === t.id} onClick={() => handleFilterChange('bodyType', localFilters.bodyType === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <PriceRange maxVal={10000000} step={5000} />
      <KeywordSection />
    </div>
  );

  // ========== JOBS FILTERS ==========
  const renderJobsFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'نوع الوظيفة' : 'Job Type'}>
        <div className="flex flex-wrap gap-1.5">
          {jobTypes.map(t => <FilterPill key={t.id} selected={localFilters.jobType === t.id} onClick={() => handleFilterChange('jobType', localFilters.jobType === t.id ? '' : t.id)} testId={`job-type-${t.id}`}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'مستوى الخبرة' : 'Experience Level'}>
        <div className="flex flex-wrap gap-1.5">
          {experienceLevels.map(t => <FilterPill key={t.id} selected={localFilters.experience === t.id} onClick={() => handleFilterChange('experience', localFilters.experience === t.id ? '' : t.id)} testId={`exp-${t.id}`}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'القطاع' : 'Industry'}>
        <div className="flex flex-wrap gap-1.5">
          {jobIndustries.map(t => <FilterPill key={t.id} selected={localFilters.subcategory === t.id} onClick={() => handleFilterChange('subcategory', localFilters.subcategory === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <FilterSection title={isAr ? 'نطاق الراتب' : 'Salary Range'}>
        <div className="grid grid-cols-2 gap-2.5 mb-3">
          <FilterInput type="number" placeholder={isAr ? 'الحد الأدنى' : 'Min Salary'} suffix="AED"
            value={localFilters.minPrice} onChange={e => handleFilterChange('minPrice', e.target.value)} testId="filter-min-salary" />
          <FilterInput type="number" placeholder={isAr ? 'الحد الأقصى' : 'Max Salary'} suffix="AED"
            value={localFilters.maxPrice} onChange={e => handleFilterChange('maxPrice', e.target.value)} testId="filter-max-salary" />
        </div>
      </FilterSection>
      <KeywordSection />
    </div>
  );

  // ========== GENERIC CATEGORY FILTERS ==========
  const renderCategoryFilters = (types, typeKey) => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <FilterSection title={isAr ? 'النوع' : 'Type'}>
        <div className="flex flex-wrap gap-1.5">
          {types.map(t => <FilterPill key={t.id} selected={localFilters[typeKey] === t.id} onClick={() => handleFilterChange(typeKey, localFilters[typeKey] === t.id ? '' : t.id)}>{t.label}</FilterPill>)}
        </div>
      </FilterSection>
      <ConditionSection />
      <PriceRange />
      <KeywordSection />
    </div>
  );

  const renderGeneralFilters = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <LocationSection />
      <ConditionSection />
      <PriceRange />
      <KeywordSection />
    </div>
  );

  const getFilterTitle = () => {
    switch (filterType) {
      case 'cars': return isAr ? 'فلاتر السيارات' : 'Car Filters';
      case 'motors': return isAr ? 'فلاتر السيارات' : 'Car Filters';
      case 'motorcycles': return isAr ? 'فلاتر الدراجات النارية' : 'Motorcycle Filters';
      case 'boats': return isAr ? 'فلاتر القوارب' : 'Boat Filters';
      case 'heavy-vehicles': return isAr ? 'فلاتر المركبات الثقيلة' : 'Heavy Vehicle Filters';
      case 'auto-accessories': return isAr ? 'فلاتر الإكسسوارات' : 'Accessories Filters';
      case 'number-plates': return isAr ? 'فلاتر لوحات الأرقام' : 'Number Plate Filters';
      case 'furniture': return isAr ? 'فلاتر الأثاث' : 'Furniture Filters';
      case 'classifieds': return isAr ? 'فلاتر الإعلانات' : 'Classifieds Filters';
      case 'community': return isAr ? 'فلاتر المجتمع' : 'Community Filters';
      case 'property': return isAr ? 'فلاتر العقارات' : 'Property Filters';
      case 'jobs': return isAr ? 'فلاتر الوظائف' : 'Job Filters';
      default: return isAr ? 'الفلاتر' : 'Filters';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="filters-modal-overlay">
      <div className="absolute inset-x-0 bottom-0 top-0 sm:top-8 bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] sm:rounded-t-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Dark Header */}
        <div className="hero-gradient relative shrink-0">
          <div className="noise-overlay" />
          <div className="flex items-center justify-between px-4 py-3.5 relative z-10">
            <button onClick={onClose} className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center hover:bg-white/15 transition-all active:scale-90" data-testid="close-filters">
              <X className="w-5 h-5 text-white" />
            </button>
            <h2 className="font-bold text-white text-base font-heading">{getFilterTitle()}</h2>
            <button onClick={handleReset} className="text-[#E31C25] text-sm font-bold hover:text-[#ff3a42] transition-colors" data-testid="reset-filters">
              {isAr ? 'مسح' : 'Reset'}
            </button>
          </div>
        </div>

        {/* Content */}
        {filterType === 'property' && renderPropertyFilters()}
        {(filterType === 'cars' || filterType === 'motors') && renderCarsFilters()}
        {filterType === 'motorcycles' && renderMotorcyclesFilters()}
        {filterType === 'boats' && renderBoatsFilters()}
        {filterType === 'heavy-vehicles' && renderHeavyVehiclesFilters()}
        {filterType === 'auto-accessories' && renderAccessoriesFilters()}
        {filterType === 'number-plates' && renderNumberPlatesFilters()}
        {filterType === 'jobs' && renderJobsFilters()}
        {filterType === 'furniture' && renderCategoryFilters(furnitureTypes, 'furnitureType')}
        {filterType === 'classifieds' && renderCategoryFilters(classifiedTypes, 'classifiedType')}
        {filterType === 'community' && renderCategoryFilters(communityTypes, 'communityType')}
        {filterType === 'general' && renderGeneralFilters()}

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06] bg-[#1A1A2E]/80 backdrop-blur-sm shrink-0 mb-16">
          <button onClick={handleApply}
            className="w-full py-3.5 bg-[#E31C25] text-white rounded-2xl font-bold text-sm hover:bg-[#C41820] transition-all shadow-lg shadow-[#E31C25]/20 active:scale-[0.98]"
            data-testid="apply-filters-btn">
            {isAr ? 'عرض النتائج' : 'Show Results'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersModal;
